import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { InMemoryFundraisersListQuery } from '../../../types/__generated__';
import PageSection, { PageSectionProps } from '../PageSection/PageSection';
import LoadMoreButton from '../LoadMoreButton/LoadMoreButton';
import useFullPageQuery from '../../hooks/useFullPageQuery';
import { onlyImages } from '../../utils/typeGuards';
import clientConfig from '../../client/config';
import styles from './InMemoryFundraisersList.scss';
import { FundraiserCard } from '../FundraiserCard/FundraiserCard';
import { InMemoryFundraisersListSkeleton } from './InMemoryFundraisersListSkeleton';
import { NetworkStatus } from '@apollo/client';
import classnames from 'classnames';
import '@justgiving/loading/src/Loading.scss';
import DOCUMENT from './InMemoryFundraisersList.gql';
import { InMemoryCreateFundraisingPageButton } from '../InMemoryCreateFundraisingPageButton/InMemoryCreateFundraisingPageButton';

export const InMemoryFundraisersList: FunctionComponent = () => {
  const { t } = useTranslation('common', { useSuspense: false });
  const { data, error, fetchMore, networkStatus, refetch } = useFullPageQuery<
    InMemoryFundraisersListQuery
  >(DOCUMENT, {
    notifyOnNetworkStatusChange: true,
  });

  const pageSectionProps: PageSectionProps = {
    title: t('inMemory.fundraisers.title'),
    hasBorder: true,
    className: styles.container,
    titleId: 'in-memory-fundraisers',
  };

  if (networkStatus === NetworkStatus.loading) {
    return (
      <PageSection {...pageSectionProps}>
        <InMemoryFundraisersListSkeleton
          animated
          displayRefreshButton={false}
        />
      </PageSection>
    );
  }

  if (error) {
    return (
      <PageSection {...pageSectionProps}>
        <InMemoryFundraisersListSkeleton onRefresh={refetch} />
      </PageSection>
    );
  }

  if (!data?.page) {
    return null;
  }

  const createFrpUrl = `${clientConfig.baseUri}/fundraising-page/creation/initiate?rmpId=${data.page.slug}`;

  if (
    !data?.page?.relationships?.members.edges ||
    data.page.relationships.members.edges.length === 0
  ) {
    // NOTE: As an IMO page is a by-product of FRP creation, there should always be at least one "member" FRP - so this is an edge case.
    return (
      <PageSection {...pageSectionProps}>
        <p className="jg-h5 jg-text--center jg-space-mbsm">
          {t('inMemory.fundraisers.noFundraisersYet')}
        </p>
        <div className={styles.button_wrapper}>
          <a
            href={createFrpUrl}
            className={classnames('jg-btn jg-btn--ghost', styles.button)}
          >
            {t('inMemory.fundraisers.becomeFirstFundraiser')}
          </a>
        </div>
      </PageSection>
    );
  }

  const {
    members: {
      edges,
      pageInfo: { hasNextPage },
    },
  } = data.page.relationships;

  async function handleFetchMore() {
    await fetchMore({
      variables: {
        after: data?.page?.relationships?.members.pageInfo.endCursor,
      },
    });
  }

  return (
    <PageSection {...pageSectionProps}>
      <ul
        data-qa="in-memory-fundraisers-list"
        className={classnames(styles.list_wrapper, 'jg-space-ptsm')}
      >
        {edges.map((fundraiser, index) => {
          const {
            title,
            owner,
            cover,
            donations,
            donationSummary,
            slug,
            targetWithCurrency,
          } = fundraiser.node;

          const imageUrl = cover && onlyImages(cover) ? cover.url : '';

          return (
            <FundraiserCard
              key={index}
              pageTitle={title}
              ownerName={owner?.name || undefined}
              ownerAvatarImageUrl={owner?.avatar || undefined}
              pageImageUrl={imageUrl}
              pageSlug={slug}
              targetAmount={targetWithCurrency?.value}
              raisedAmount={donationSummary?.totalAmount?.value}
              currencyCode={donationSummary?.totalAmount?.currencyCode}
              supporterCount={donations?.totalCount ?? 0}
            />
          );
        })}
      </ul>
      <div className={styles.button_wrapper}>
        {hasNextPage && (
          <LoadMoreButton
            className={classnames('jg-btn', styles.button)}
            loading={networkStatus === NetworkStatus.fetchMore}
            onClick={handleFetchMore}
            trackingData={{
              action_name: 'click',
              subtype: 'button',
              event: 'click',
              event_value: 'show more',
              sub_section: 'in memory fundraisers',
            }}
            trackingIdentifier="in-memory-show-more-fundraisers-button"
          >
            {t('inMemory.fundraisers.showMoreFundraisers')}
          </LoadMoreButton>
        )}
        <InMemoryCreateFundraisingPageButton
          firstNameOfDeceased={data.page.inMemory?.firstNameOfDeceased}
          className={styles.button}
          trackingIdentifier="in-memory-fundraiser-list-make-a-fundraising-page-button"
        />
      </div>
    </PageSection>
  );
};
